/* importing filenamify/browser only imports filenamify and not filenamifyPath,
 * which relies on path being available or polyfilled. Importing filenamify this
 * way is therefore useful when it is shipped using webpack or similar tools,
 * and if filenamifyPath is not needed. Also, without path, Storybook will fail
 * if the entire package is imported */
// @ts-expect-error
// eslint-disable-next-line import/no-unresolved
import filenamify from 'filenamify/browser';
import { escapeRegExp } from 'lodash';
import type { DownloadItem } from '../hooks/useGetAssetDownloadItems';

/* File Extension Determination
 * In the case of files imported from the web app, the item.filename will likely
 * have an extension, however in the case of Sequences imported from Premiere,
 * it is unlikely that the asset name (Sequence Stack name) will have an
 * extension. Therefore, we can apply some logic to determine the most likely
 * extension for the download based on item.pathname or the
 * response-content-type from the searchParams.
 *
 * Note: It is also possible that the filename in the
 * response-content-disposition will also have an extension, but that extension
 * is only present when the item.pathname also indicates an ext. Since the
 * item.pathname is more reliable because it has been normalized (using a UUID
 * pathname and server-assigned filename) and should not contain any '.' as
 * special characters, it is preferred.
 */
export const normalizeDownloadItem = (item: DownloadItem) => {
  const itemURL = new URL(item.url);
  const pathnameExt = getExtension(itemURL.pathname);
  const itemFilename =
    sanitizeAssetName(sanitizeFilename(item.filename)) || 'importedAsset';
  const itemFileNameExt = getExtension(item.filename);
  const mimeType = itemURL.searchParams.get('response-content-type') || '';
  const typeExt = mimeTypeToExtensionMap[mimeType.toLowerCase()] || '';

  const downloadFileName =
    (itemFileNameExt &&
      (itemFilename === `.${itemFileNameExt}`
        ? `importedAsset.${itemFileNameExt}`
        : itemFilename)) ||
    (pathnameExt && `${itemFilename}.${pathnameExt}`) ||
    (typeExt && `${itemFilename}.${typeExt}`) ||
    itemFilename;

  /* The item.path will end with the given item.filename (which may not be a
   * valid for writing to disk). Therefore we need to replace the filename
   * portion of the path with the sanitized downloadFileName to ensure that the
   * path is valid for writing to disk (and contains an extension if it can be
   * determined).
   */
  const safePath = item.path.replace(
    // @ts-ignore-next-line
    new RegExp(`${escapeRegExp(item.filename)}$`),
    downloadFileName
  );

  return {
    ...item,
    path: safePath,
  };
};

const getExtension = (filename: string) => {
  return /\.([a-zA-Z0-9]+$)/.exec(filename)?.[1] || '';
};

const sanitizeFilename = (filename: string) => {
  // ReadSettingsFromSourceSequence in DVA has a max length of 230
  return filenamify(filename, {
    maxLength: 230,
    replacement: '',
  });
};

/* additionally sanitize asset names by removing characters [#] that do not play
 * well with our filesystem operations (although technically # should be valid)
 */
const sanitizeAssetName = (assetName: string) => {
  return assetName.replace(/[#]/g, '');
};

// https://helpx.adobe.com/ie/premiere/desktop/organize-media/import-files/supported-file-formats.html
const mimeTypeToExtensionMap: Record<string, string> = {
  'application/mxf': 'mxf', // MXF video (Material Exchange Format)
  'audio/3gpp': '3gp', // Multimedia container format
  'audio/3gpp2': '3g2', // Multimedia container format
  'audio/aac': 'aac', // Advanced Audio Coding
  'audio/aiff': 'aiff', // Audio Interchange File Format
  'audio/mpeg': 'mp3', // MP3 audio
  'audio/wav': 'wav', // Waveform Audio File Format
  'audio/x-aac': 'aac', // Advanced Audio Coding
  'audio/x-aiff': 'aiff', // Audio Interchange File Format
  'image/gif': 'gif', // Graphics Interchange Format
  'image/heif': 'heif', // High Efficiency Image Coding (format for still images that uses the HEVC video codec)
  'model/vnd.mts': 'mts', // MTS video (MPEG Transport Stream)
  'video/3gpp': '3gp', // Multimedia container format
  'video/3gpp2': '3g2', // Multimedia container format
  'video/avchd-stream': 'mts', // AVCHD
  'video/avi': 'avi', // Audio Video Interleave (DV-AVI, Microsoft AVI Type 1 and Type 2)
  'video/h264': 'h264', // H.264 video (Advanced Video Coding (AVC))
  'video/mp2t': 'm2ts', // Blu-ray BDAV MPEG-2 Transport Stream, AVCHD
  'video/mp4': 'mp4', // QuickTime Movie, XDCAM EX
  'video/mpeg': 'mpg', // MPEG Video
  'video/msvideo': 'avi', // Audio Video Interleave (DV-AVI, Microsoft AVI Type 1 and Type 2)
  'video/mts': 'mts', // AVCHD
  'video/quicktime': 'mov', // QuickTime Movie
  'video/vnd.dlna.mpeg-tts': 'm2ts', // Blu-ray BDAV MPEG-2 Transport Stream, AVCHD
  'video/x-dv': 'dv', // Digital Video
  'video/x-m4v': 'm4v', // MPEG-4 video (M4V)
  'video/x-mpeg': 'mpg', // MPEG Video
  'video/x-ms-asf': 'asf', // NetShow (Windows only)
  'video/x-ms-vob': 'vob', // DVD Video Object
  'video/x-ms-wmv': 'wmv', // Windows Media Video
  'video/x-msvideo': 'avi', // Audio Video Interleave (DV-AVI, Microsoft AVI Type 1 and Type 2)
};
